'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Icon, type IconName } from '@/components/ui/icon';
import { signOut } from '@/lib/actions/auth';
import styles from './mobile-nav.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

interface NavLink {
  href: string;
  label: string;
  icon?: IconName;
}

interface Props {
  links: readonly NavLink[];
  isAuthenticated: boolean;
  /** Account initials + email for the drawer identity block (signed-in). */
  initials?: string;
  email?: string;
}

export function MobileNav({ links, isAuthenticated, initials, email }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  // `entered` flips true one frame after mount so the scrim fade + slide-in
  // transition from their initial (closed) state actually animate.
  const [entered, setEntered] = useState(false);
  const pathname = usePathname();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const t = useTranslations('nav');
  const tc = useTranslations('common');

  function close() {
    setIsOpen(false);
  }

  function open() {
    setIsOpen(true);
  }

  const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);

  // Drive the enter animation: flip `entered` one frame after open (so the
  // transition runs), and reset it on close/unmount via cleanup for next time.
  useEffect(() => {
    if (!isOpen) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => {
      cancelAnimationFrame(raf);
      setEntered(false);
    };
  }, [isOpen]);

  // Move focus to close button when drawer opens; return to trigger on close
  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
    } else {
      // Only restore focus to trigger if it was a keyboard-initiated close
      triggerRef.current?.focus();
    }
  }, [isOpen]);

  // Close drawer on Escape key
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Close drawer when viewport reaches desktop width (avoids invisible open drawer)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    function handleChange(e: MediaQueryListEvent) {
      if (e.matches) close();
    }
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  // Focus trap: keep Tab/Shift+Tab within the drawer
  useEffect(() => {
    if (!isOpen) return;
    function handleTab(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const drawer = document.getElementById('mobile-nav-drawer');
      if (!drawer) return;
      const focusable = Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.menuButton}
        onClick={open}
        aria-label={t('openMenu')}
        aria-expanded={isOpen}
        aria-controls="mobile-nav-drawer"
      >
        <Icon name="menu" size={20} aria-hidden="true" />
      </button>

      {/* Portal to body so the drawer escapes the header's stacking context. */}
      {isOpen && createPortal(
        <div className={cx(styles.root, entered && styles.open)}>
          <div className={styles.scrim} onClick={close} aria-hidden="true" />

          <aside
            id="mobile-nav-drawer"
            className={styles.drawer}
            role="dialog"
            aria-modal="true"
            aria-label={t('navigationMenu')}
          >
            <div className={styles.head}>
              <span className={styles.wordmark}>{tc('appName')}</span>
              <button
                ref={closeButtonRef}
                type="button"
                className={styles.iconBtn}
                onClick={close}
                aria-label={t('closeMenu')}
              >
                <Icon name="x" size={18} aria-hidden="true" />
              </button>
            </div>

            <nav className={styles.nav} aria-label={t('mobileNavigation')}>
              {links.map(({ href, label, icon }) => (
                <Link
                  key={href}
                  href={href}
                  className={cx(styles.link, isActive(href) && styles.linkActive)}
                  aria-current={isActive(href) ? 'page' : undefined}
                  onClick={close}
                >
                  {icon && <Icon name={icon} size={18} strokeWidth={1.75} aria-hidden="true" />}
                  <span className={styles.linkLabel}>{label}</span>
                </Link>
              ))}
            </nav>

            <div className={styles.sep} />
            <ThemeToggle variant="row" />
            <div className={styles.sep} />

            {isAuthenticated ? (
              <>
                <div className={styles.acct}>
                  <span className={styles.avatar} aria-hidden="true">{initials}</span>
                  {email && <span className={styles.acctEmail}>{email}</span>}
                </div>
                <nav className={styles.nav}>
                  <Link href="/profile" className={styles.link} onClick={close}>
                    <Icon name="user" size={18} strokeWidth={1.75} aria-hidden="true" />
                    <span className={styles.linkLabel}>{t('profile')}</span>
                  </Link>
                  <Link href="/settings" className={styles.link} onClick={close}>
                    <Icon name="settings" size={18} strokeWidth={1.75} aria-hidden="true" />
                    <span className={styles.linkLabel}>{t('settings')}</span>
                  </Link>
                  <Link href="/cookbook/jobs" className={styles.link} onClick={close}>
                    <Icon name="list" size={18} strokeWidth={1.75} aria-hidden="true" />
                    <span className={styles.linkLabel}>{t('generationHistory')}</span>
                  </Link>
                  <form action={signOut} className={styles.signOutForm}>
                    <button type="submit" className={cx(styles.link, styles.linkDanger)}>
                      <Icon name="logout" size={18} strokeWidth={1.75} aria-hidden="true" />
                      <span className={styles.linkLabel}>{t('signOut')}</span>
                    </button>
                  </form>
                </nav>
              </>
            ) : (
              <div className={styles.auth}>
                <Link href="/auth/login" className={cx(styles.cta, styles.ctaGhost)} onClick={close}>
                  {t('signIn')}
                </Link>
                <Link href="/auth/signup" className={cx(styles.cta, styles.ctaPrimary)} onClick={close}>
                  {t('signUp')}
                </Link>
              </div>
            )}
          </aside>
        </div>,
        document.body,
      )}
    </>
  );
}
